import {
  type InfiniteData,
  type QueryFunctionContext,
  type UseInfiniteQueryResult,
  useInfiniteQuery,
} from '@tanstack/react-query';

import { memberService } from '@/services/member-service';
import type { Member } from '@/types/core/member';
import type { Page } from '@/types/shared/page';
import type { UUID } from '@/utils/uuid';

import { memberQueryKeys } from './query-keys';

/**
 * List members for a given tenant.
 * Accepts optional paging args via `opts`.
 */
export function useListMembers(
  tenantId: UUID,
  opts?: {
    limit?: number;
    next?: string;
    filter?: string;
  }
): UseInfiniteQueryResult<InfiniteData<Page<Member>, unknown>, Error> {
  return useInfiniteQuery<
    Page<Member>,
    Error,
    InfiniteData<Page<Member>, unknown>
  >({
    queryKey: memberQueryKeys.list(tenantId, opts?.limit, opts?.filter),
    queryFn: (context: QueryFunctionContext) => {
      const pageParam = context.pageParam as string | undefined;
      return memberService.listMembers(tenantId, {
        limit: opts?.limit,
        next: pageParam,
        filter: opts?.filter,
      });
    },
    initialPageParam: opts?.next ?? undefined,
    getNextPageParam: (last) => last.next ?? undefined,
    getPreviousPageParam: (first) => first.prev ?? undefined,
  });
}
